import { createClient } from '@supabase/supabase-js';
import { handleError } from '../utils/errorHandling';
import type { RealtimeChannel } from '@supabase/supabase-js';

// Get environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Missing Supabase environment variables');
} else {
  try {
    const maskedUrl = String(supabaseUrl).replace(/([^:]*:\/\/)([^.]+)\.(.*)/, '$1***.$3');
    const maskedKey = String(supabaseAnonKey).slice(0, 6) + '***';
    console.log('[Supabase env]', { url: maskedUrl, keyPrefix: maskedKey, mode: import.meta.env.MODE });
  } catch {
    // Ignorer feil ved logging
  }
}

// Create a single client instance for regular use
export const supabase = createClient(supabaseUrl || '', supabaseAnonKey || '', {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storageKey: 'condo_auth_token',
    // Viktig: bruk ren string storage. Supabase håndterer serialisering selv.
    storage: {
      getItem: (key) => localStorage.getItem(key),
      setItem: (key, value) => localStorage.setItem(key, value as unknown as string),
      removeItem: (key) => localStorage.removeItem(key)
    }
  },
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  }
});

// For admin-operasjoner, bruk getServiceClient fra utils/security.ts
// Dette gir bedre sikkerhet og feilhåndtering for admin-tilgang

// Realtime subscriptions helper (unused currently)

// Robust subscribe helper med backoff og automatisk re-subscribing
const activeRealtime: Record<string, { unsubscribe: () => void } | undefined> = {};

export function subscribeWithRetry(
  key: string,
  createChannel: () => RealtimeChannel,
  // eslint-disable-next-line no-unused-vars
  options?: { maxRetries?: number; baseDelayMs?: number; onStatusChange?: (status: string) => void }
) {
  let retries = 0;
  const maxRetries = options?.maxRetries ?? 5;
  const baseDelayMs = options?.baseDelayMs ?? 1000;
  let cancelled = false;
  let channel: RealtimeChannel | null = null;
  let firstSubscribedAt: number | null = null;

  const log = (...args: unknown[]) => {
     
    console.log('[Realtime]', key, ...args);
  };

  const subscribe = () => {
    if (cancelled) return;
    try {
      // Fjern eksisterende aktiv kanal for samme key før ny subscribe
      try {
        const existing = activeRealtime[key];
        if (existing) {
          existing.unsubscribe();
          activeRealtime[key] = undefined;
        }
      } catch {
    // Ignorer feil ved logging
  }
      channel = createChannel();
      const unsubscribe = () => {
        try {
          if (channel) supabase.removeChannel(channel);
        } catch (err) {
          log('removeChannel error:', err);
        }
      };
      activeRealtime[key] = { unsubscribe };
      channel.subscribe((st) => {
        options?.onStatusChange?.(st);
        log('status:', st);
        if (st === 'SUBSCRIBED') {
          retries = 0;
          firstSubscribedAt = Date.now();
        } else if (st === 'CLOSED' || st === 'CHANNEL_ERROR' || st === 'TIMED_OUT') {
          if (cancelled) return;
          const sinceFirst = firstSubscribedAt ? Date.now() - firstSubscribedAt : null;
          // Hvis kanalen lukker raskt, unngå uendelige forsøk
          const fastClose = sinceFirst !== null && sinceFirst < 2000;
          if (retries >= maxRetries) {
            log('max retries reached, giving up');
            return;
          }
          if (fastClose && retries >= 1) {
            log('closed quickly after subscribe; aborting further retries');
            return;
          }
          retries += 1;
          const delay = Math.min(baseDelayMs * 2 ** (retries - 1), 30000);
          log(`retrying in ${delay}ms... (attempt ${retries}/${maxRetries})`);
          setTimeout(() => {
            if (cancelled) return;
            try {
              unsubscribe();
            } catch (err) {
              log('removeChannel error:', err);
            }
            subscribe();
          }, delay);
        }
      });
    } catch (e) {
      log('subscribe error:', e);
    }
  };

  subscribe();

  return () => {
    cancelled = true;
    try {
      const existing = activeRealtime[key];
      if (existing) existing.unsubscribe();
      activeRealtime[key] = undefined;
    } catch (err) {
      log('removeChannel error:', err);
    }
  };
}

// Safe query helper with error handling
export const safeQuery = async <T>(
  query: Promise<{ data: T | null; error: any }>
): Promise<T> => {
  try {
    const { data, error } = await query
    if (error) throw error
    if (!data) throw new Error('No data returned')
    return data
  } catch (error) {
    throw handleError(error)
  }
}

// Add health check function
export const checkSupabaseConnection = async () => {
  try {
    // First check if we have valid credentials
    if (!supabaseUrl || !supabaseAnonKey) {
      throw new Error('Missing Supabase credentials');
    }

    // Then try to make a simple query
    const { error } = await supabase.from('profiles').select('count');
    if (error) throw error;
    
    return true;
  } catch (error) {
    console.error('Supabase connection error:', error);
    return false;
  }
};